import googleAnalytics from "@analytics/google-analytics";
import { AuthenticationError } from "@auth0/auth0-react";
import { Channel, invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { attachConsole } from "@tauri-apps/plugin-log";
import { relaunch } from "@tauri-apps/plugin-process";
import { useMachine } from "@xstate/react";
import Analytics from "analytics";
import { isEmpty } from "lodash";
import NextApp, { type AppContext, type AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useEffectOnce, useInterval } from "react-use";
import { AnalyticsProvider } from "use-analytics";

import "@fontsource-variable/inter/wght.css";
import "@fontsource/ibm-plex-mono/latin.css";
import "@moment/design-system/main.css";
import "@xterm/xterm/css/xterm.css";

import { type AppConfig, appConfigSchema } from "@moment/api-collab/api-types";
import { ToastNotifications, makeToast } from "@moment/design-system/Toast";
import { type UserInfo, setUserProperty } from "@moment/logging";

import { type ClientActionsProps } from "~/ClientActions";
import { Providers } from "~/Providers";
import { isDesktopAppMode } from "~/auth/interop/common";
import { onLoginSuccess } from "~/auth/loginAction";
import { useAuth } from "~/auth/useAuth";
import { useLogger } from "~/components/canvas/hooks/useLogger";
import { FatalAppError } from "~/components/errors/FatalAppError";
import { FindInPage } from "~/components/find-in-page/FindInPage";
import { useOutdatedAppModal } from "~/components/outdated-app/OutdatedAppModalContext";
import { Head } from "~/components/seo/Head";
import { exists, getConfigFilePath, readJsonFile, writeJsonFile } from "~/data/filesystem";
import { useAtlasSettingsState } from "~/state";
import { autoUpdaterMachine } from "~/state/machines/desktop/autoupdater.machine";
import { type DownloadEvent } from "~/state/types";
import { newFetch } from "~/utils/atlas";
import { initLogger } from "~/utils/datadog/datadog";
import { isLocal } from "~/utils/env";
import { printEnvironment } from "~/utils/printEnvironment";

export type MomentAppProps = AppProps & {
	cookies?: string;
};

const ClientActions = dynamic<ClientActionsProps>(
	() => import("~/ClientActions").then((m) => m.ClientActions),
	// turn off server side rendering for this code
	{ ssr: false }
);

const App = (props: MomentAppProps) => {
	const { pageProps } = props;

	return (
		<>
			<Head
				// pageProps comes in as "any" so we need to check for existence
				title={pageProps?.title ?? null}
				description={pageProps?.description ?? null}
				url={pageProps?.url ?? null}
			/>
			<ClientActions />
			<ErrorBoundary FallbackComponent={FatalAppError}>
				<Providers pageProps={pageProps}>
					<Main {...props} />
					{/* <SpeedInsights /> */}
				</Providers>
			</ErrorBoundary>
		</>
	);
};

const getMeasurementIds = () => {
	if (process.env.APP_ENV === "production") {
		return "G-PBWVPLX26V";
	} else {
		return "G-4JR0PBCCBT";
	}
};

const analytics = Analytics({
	app: "Moment App",
	plugins: [
		googleAnalytics({
			measurementIds: getMeasurementIds(),
		}),
	],
});

// Use a variable to prevent running
// init routine for desktop app twice.
// https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
let didRunDesktopAppInit = false;

interface UpdateMetadata {
	latestVersion: string;
	currentVersion: string;
}

export const Main = ({ Component, pageProps }: AppProps) => {
	const { confirm } = useOutdatedAppModal();
	const [showFindInPage, setShowFindInPage] = useState(false);
	const { loginWithRedirect, error: authError, user } = useAuth();
	const router = useRouter();

	const atlasSettings = useAtlasSettingsState();

	// we use this to save the original fetch function.
	// we need to do this because we want to fallback to the original fetch function
	// if the user disables proxying via atlas..
	const [originalFetch] = useState(() => {
		return globalThis.fetch;
	});

	const [result, send] = useMachine(autoUpdaterMachine);

	// disable canvas caching for local-first for now
	/*
	useEffect(() => {
		if (!wb) {
			return;
		}

		// Pre-caching core canvases requires the user's auth info,
		// so don't trigger pre-caching if the user is not logged-in
		// yet.
		if (authState !== AuthStates.LoggedIn) {
			return;
		}

		// The docs for `messageSW()` state that the service worker
		// should set a response using `event.ports[0].postMessage(...)`
		// or the promise here won't resolve.
		wb.messageSW({
			type: "PRE_CACHE",
			urls: coreCanvases.map((c) => `/api/${c}?env=${env}`),
		}).catch((err) =>
			console.error("Error sending pre-caching message to the service worker", err)
		);
	}, [authState, env]);
	 */

	const logger = useLogger("Main");

	const fetchUpdate = useCallback(async () => {
		const onEvent = new Channel<DownloadEvent>();
		onEvent.onmessage = (message) => {
			send({ type: "update", message });
		};

		let updateMetadata: UpdateMetadata | undefined = undefined;
		try {
			// We don't set endpoints for local mode for auto-updating.
			// THus to avoid unnecessary error logs, we skip this check.
			// if we need to test auto-updating,
			// we need to set an endpoint in tauri.conf.json.
			if (isLocal) {
				return;
			}
			const value = await invoke("fetch_update");
			if (!value) {
				return;
			}

			updateMetadata = value as UpdateMetadata;
			if (updateMetadata.latestVersion === updateMetadata.currentVersion) {
				send({ type: "reset" });
				return;
			}

			console.log("Update is available", updateMetadata);
		} catch (err) {
			console.error("Failed to check for updates", err);
		}

		if (!updateMetadata) {
			return;
		}

		try {
			await invoke("prepare_update", { onEvent });
		} catch (err) {
			console.error("Failed to download update", err);
		}
	}, [send]);

	useEffect(() => {
		if (!isDesktopAppMode()) {
			return;
		}

		if (didRunDesktopAppInit) {
			return;
		}

		didRunDesktopAppInit = true;
		void attachConsole();

		void listen("show-find-in-page", () => setShowFindInPage(true));
		void listen("check-for-updates", () => fetchUpdate());
		void fetchUpdate();

		void listen("show-error", (event) => {
			const message =
				"payload" in event && event.payload && event.payload["message"]
					? event.payload["message"]
					: "unknown error";
			makeToast({
				message,
				variant: "error",
			});
		});
	}, [fetchUpdate, loginWithRedirect]);

	// TODO: move this to the tauri layer
	useInterval(
		() => {
			fetchUpdate().catch(logger.error);
		},
		1000 * 60 * 60
	);

	// handle loading config.
	// we only really seem to use this for the anonID, which is used for datadog.
	// originally, I wanted to read this via createQueryMachine,
	// but was fighting with the types way too much.
	useEffectOnce(() => {
		if (!isDesktopAppMode()) {
			return;
		}

		void (async () => {
			const configPath = await getConfigFilePath();
			const configExists = await exists(configPath);
			if (!configExists) {
				// create an anonymous user ID
				const config = appConfigSchema.parse({});
				setUserProperty("anonID", config.userID);
				await writeJsonFile(configPath, config);
				return;
			}

			const config = await readJsonFile<AppConfig>(configPath);
			const anonUserID = config?.userID;
			if (!isEmpty(anonUserID)) {
				setUserProperty("anonID", anonUserID);
			}
		})();
	});

	// reload fetch if atlas proxy settings changes
	useEffect(() => {
		if (originalFetch === undefined) {
			return;
		}

		// we do not support the browser as of today.
		if (!isDesktopAppMode()) {
			return;
		}

		globalThis.fetch = newFetch(atlasSettings.settings, originalFetch);
		console.debug("AtlasProvider: updated fetch function", atlasSettings.settings);
	}, [atlasSettings.settings, originalFetch]);

	useEffect(() => {
		const datadogUser: UserInfo = {
			id: "",
			email: "",
			isMoment: false,
			loggedIn: false,
		};

		if (!isEmpty(user)) {
			datadogUser.id = user.id;
			datadogUser.loggedIn = true;
			datadogUser.isMoment = user.isMomentEmployee;
			datadogUser.email = user.email;
			datadogUser.name = user.name;
			void analytics.identify(user.id, {
				isDesktop: isDesktopAppMode(),
			});
		}

		if (datadogUser.id !== "") {
			setUserProperty("id", datadogUser.id);
			setUserProperty("email", datadogUser.email);
			setUserProperty("isMoment", datadogUser.isMoment);
			setUserProperty("loggedIn", datadogUser.loggedIn);
			setUserProperty("name", datadogUser.name);
		}
	}, [user]);

	// Watch for postlogin failure
	useEffect(() => {
		const { postlogin, postlogout } = router.query;
		if (postlogin === "true" || postlogout === "true") {
			if (authError) {
				console.log(authError);
				makeToast({
					message:
						"There was an error with your login. Please try again or contact support@moment.dev",
					timer: "dismissible",
					variant: "error",
				});

				if (postlogin === "true" && authError instanceof AuthenticationError) {
					const auth0Error = authError as AuthenticationError;
					const returnTo = onLoginSuccess(auth0Error.appState["redirectId"]);
					if (returnTo) {
						void router.replace({ query: {}, pathname: returnTo }, undefined, {
							shallow: true,
						});
					}
				}
			}
		}
	}, [authError, router]);

	useEffectOnce(() => {
		printEnvironment();
	});

	useEffect(() => {
		if (!result.context.autoUpdaterState.isOutdatedAppVersion) {
			return;
		}

		confirm()
			.then(async (confirmed) => {
				if (!confirmed) {
					return;
				}

				// relaunch BYPASSES shutdown hooks. NOT OKAY.
				await invoke("stop_atlas");
				await relaunch();
			})
			.catch((err) =>
				logger.error("Failed to show dialog for min version enforcement", {
					error: `${err}`,
				})
			);
	}, [confirm, logger, result]);

	useEffectOnce(() => {
		initLogger();
	});

	return (
		<AnalyticsProvider instance={analytics}>
			{showFindInPage && <FindInPage onClose={() => setShowFindInPage(false)} />}
			<Component {...pageProps} />
			<ToastNotifications />
		</AnalyticsProvider>
	);
};

App.getInitialProps = async (context: AppContext) => {
	const appProps = NextApp.getInitialProps(context);

	return { ...appProps, cookies: context.ctx.req?.headers.cookie };
};

export default App;
